import { X } from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useCallback } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useAudio, useConfig, sessionState } from '@chainlit/react-client';
import { activeAudioButtonState } from '@/state/chat';

import AudioPresence from '@/components/AudioPresence';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useUISettings } from '@/hooks/useUISettings';
import { Translator } from 'components/i18n';

import { Loader } from '../../Loader';
import { VoiceLines } from '../../icons/VoiceLines';
import { Button } from '../../ui/button';

interface Props {
  disabled?: boolean;
}

const VoiceButton = ({ disabled }: Props) => {
  const { config } = useConfig();
  const { settings, isLoading } = useUISettings();
  const { startConversation, endConversation, audioConnection } = useAudio();
  const [activeButton, setActiveButton] = useRecoilState(activeAudioButtonState);
  const session = useRecoilValue(sessionState);

  console.log('[Assistant] VoiceButton - Component render:', {
    config: config?.features.audio.enabled,
    settings: settings?.voiceMode,
    isLoading
  });

  // Check both chainlit config and UI settings
  const isEnabled = !!config?.features.audio.enabled && !!settings?.voiceMode;
  
  console.log('[Assistant] VoiceButton - isEnabled:', isEnabled);
  console.log('[Assistant] VoiceButton - audioConnection:', audioConnection);

  // Emit audio source selection before starting conversation
  const handleStartStandardConversation = useCallback(() => {
    console.log('[Assistant] handleStartStandardConversation called');
    console.log('[Assistant] Socket connected:', session?.socket?.connected);
    
    if (session?.socket) {
      // Emit audio_source_select event with standard type
      session.socket.emit('audio_source_select', { sourceType: 'standard' });
      console.log('[Assistant] Emitted audio_source_select with sourceType: standard');
    }
    
    // Mark this button as active
    setActiveButton('standard');
    
    // Start the conversation
    startConversation();
  }, [startConversation, setActiveButton, session]);
  
  const handleEndConversation = useCallback(() => {
    endConversation();
    setActiveButton(null);
  }, [endConversation, setActiveButton]);

  useHotkeys(
    'p',
    () => {
      if (!isEnabled) return;
      if (audioConnection === 'on') return endConversation();
      return handleStartStandardConversation();
    },
    [isEnabled, audioConnection, handleStartStandardConversation, endConversation]
  );

  // Don't render until settings are loaded
  if (isLoading) {
    return null;
  }

  if (!isEnabled) return null;
  
  // Only show as active if this button initiated the recording
  const isThisButtonActive = audioConnection === 'on' && activeButton === 'standard';
  // Disable when assistant button is active
  const isDisabled = disabled || (audioConnection === 'on' && activeButton === 'assistant');

  return (
    <div className="flex items-center gap-1">
      {isThisButtonActive ? (
        <AudioPresence
          type="client"
          height={18}
          width={36}
          barCount={4}
          barSpacing={2}
        />
      ) : null}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              disabled={isDisabled}
              variant="ghost"
              size="icon"
              className="hover:bg-muted"
              onClick={
                isThisButtonActive
                  ? handleEndConversation
                  : audioConnection === 'off'
                  ? handleStartStandardConversation
                  : undefined
              }
            >
              {isThisButtonActive ? <X className="!size-5" /> : null}
              {!isThisButtonActive && audioConnection !== 'connecting' ? (
                <VoiceLines className="!size-6" />
              ) : null}
              {audioConnection === 'connecting' && activeButton === 'standard' ? (
                <Loader className="!size-5" />
              ) : null}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              <Translator
                path={
                  audioConnection === 'on'
                    ? 'chat.speech.stop'
                    : audioConnection === 'off'
                    ? 'chat.speech.start'
                    : 'chat.speech.connecting'
                }
                suffix=" (P)"
              />
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
export default VoiceButton;
